import React, { useState, useEffect } from 'react';
import { Header } from '../components/Header';
import { PageLoader } from '../components/LoadingSpinner';
import { formatCurrency, formatNumber } from '../lib/formatters';
import { Trophy, Medal, Award, Crown, TrendingUp, Users, Star } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

export default function LeaderboardPage() {
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [timeframe, setTimeframe] = useState('all'); // all, month, week

  useEffect(() => {
    fetchLeaderboard();
  }, [timeframe]);

  const fetchLeaderboard = async () => {
    try {
      setLoading(true);
      const response = await api.get('/leaderboard', { params: { timeframe } });
      setLeaderboard(response.data);
    } catch (error) {
      console.error('Failed to fetch leaderboard:', error);
      toast.error('Failed to load leaderboard');
    } finally {
      setLoading(false);
    }
  };

  const getRankIcon = (rank) => {
    switch (rank) {
      case 1:
        return <Crown className="w-8 h-8 text-yellow-500" />;
      case 2:
        return <Medal className="w-7 h-7 text-gray-400" />;
      case 3:
        return <Award className="w-7 h-7 text-amber-600" />;
      default:
        return <span className="text-2xl font-bold text-gray-400 font-mono">#{rank}</span>;
    }
  };

  const getRankBg = (rank) => {
    switch (rank) {
      case 1:
        return 'bg-gradient-to-r from-yellow-50 to-amber-50 border-yellow-200';
      case 2:
        return 'bg-gradient-to-r from-gray-50 to-slate-50 border-gray-200';
      case 3:
        return 'bg-gradient-to-r from-amber-50 to-orange-50 border-amber-200';
      default:
        return 'bg-white border-[hsl(var(--border))]';
    }
  };

  // Calculate stats
  const totalInvestors = leaderboard.length;
  const totalSharesOwned = leaderboard.reduce((sum, u) => sum + u.total_shares, 0);
  const totalInvested = leaderboard.reduce((sum, u) => sum + u.portfolio_value, 0);

  return (
    <div className="min-h-screen bg-[hsl(var(--background))]">
      <Header />

      <main className="max-w-4xl mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8">
        {/* Page Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-yellow-400 to-amber-500 rounded-full mb-4 shadow-lg">
            <Trophy className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-[hsl(var(--text-primary))] mb-2">
            Top Shareholders
          </h1>
          <p className="text-[hsl(var(--text-secondary))]">
            See who's leading the investment game at HUB Fleet Shares
          </p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-3 gap-4 mb-8">
          <div className="bg-white border border-[hsl(var(--border))] rounded-xl p-4 text-center">
            <Users className="w-6 h-6 text-[hsl(var(--brand-primary))] mx-auto mb-2" />
            <div className="text-2xl font-bold font-mono text-[hsl(var(--text-primary))]">{formatNumber(totalInvestors)}</div>
            <div className="text-xs text-[hsl(var(--text-secondary))]">Total Investors</div>
          </div>
          <div className="bg-white border border-[hsl(var(--border))] rounded-xl p-4 text-center">
            <TrendingUp className="w-6 h-6 text-green-600 mx-auto mb-2" />
            <div className="text-2xl font-bold font-mono text-[hsl(var(--text-primary))]">{formatNumber(totalSharesOwned)}</div>
            <div className="text-xs text-[hsl(var(--text-secondary))]">Shares Owned</div>
          </div>
          <div className="bg-white border border-[hsl(var(--border))] rounded-xl p-4 text-center">
            <Star className="w-6 h-6 text-amber-500 mx-auto mb-2" />
            <div className="text-2xl font-bold font-mono text-[hsl(var(--text-primary))]">{formatCurrency(totalInvested)}</div>
            <div className="text-xs text-[hsl(var(--text-secondary))]">Total Invested</div>
          </div>
        </div>

        {/* Timeframe Filter */}
        <div className="flex justify-center gap-2 mb-6">
          {[
            { value: 'all', label: 'All Time' },
            { value: 'month', label: 'This Month' },
            { value: 'week', label: 'This Week' },
          ].map((option) => (
            <button
              key={option.value}
              onClick={() => setTimeframe(option.value)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                timeframe === option.value
                  ? 'bg-[hsl(var(--brand-primary))] text-white'
                  : 'bg-white border border-[hsl(var(--border))] text-[hsl(var(--text-secondary))] hover:bg-[hsl(var(--background-secondary))]'
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>

        {/* Leaderboard */}
        {loading ? (
          <PageLoader text="Loading leaderboard..." />
        ) : leaderboard.length === 0 ? (
          <div className="bg-white border border-[hsl(var(--border))] rounded-xl p-12 text-center">
            <Trophy className="w-12 h-12 text-[hsl(var(--text-tertiary))] mx-auto mb-4" />
            <p className="text-[hsl(var(--text-secondary))]">No shareholders yet</p>
            <p className="text-sm text-[hsl(var(--text-tertiary))] mt-1">Be the first to invest!</p>
          </div>
        ) : (
          <div className="space-y-3">
            {leaderboard.map((user, index) => {
              const rank = index + 1;
              return (
                <div
                  key={user.user_id}
                  data-testid={`leaderboard-rank-${rank}`}
                  className={`${getRankBg(rank)} border rounded-xl p-4 md:p-5 flex items-center gap-4 transition-all hover:shadow-md ${rank <= 3 ? 'shadow-sm' : ''}`}
                >
                  {/* Rank */}
                  <div className="w-12 h-12 flex items-center justify-center flex-shrink-0">
                    {getRankIcon(rank)}
                  </div>

                  {/* User Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h3 className={`font-semibold truncate ${rank === 1 ? 'text-lg' : 'text-base'} text-[hsl(var(--text-primary))]`}>
                        {user.name}
                      </h3>
                      {rank === 1 && (
                        <span className="px-2 py-0.5 bg-yellow-100 text-yellow-800 text-xs font-bold rounded-full">
                          TOP INVESTOR
                        </span>
                      )}
                    </div>
                    <div className="text-sm text-[hsl(var(--text-secondary))]">
                      {user.assets_count} {user.assets_count === 1 ? 'asset' : 'assets'} • {formatNumber(user.total_shares)} shares
                    </div>
                  </div>

                  {/* Portfolio Value */}
                  <div className="text-right flex-shrink-0">
                    <div className={`font-bold font-mono ${rank === 1 ? 'text-2xl text-yellow-600' : rank <= 3 ? 'text-xl text-[hsl(var(--brand-primary))]' : 'text-lg text-[hsl(var(--text-primary))]'}`}>
                      {formatCurrency(user.portfolio_value)}
                    </div>
                    <div className="text-xs text-[hsl(var(--text-tertiary))]">portfolio value</div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Motivational Message */}
        {leaderboard.length > 0 && (
          <div className="mt-8 bg-gradient-to-r from-[hsl(var(--brand-primary))] to-[hsl(var(--brand-primary-dark))] rounded-xl p-6 text-center text-white">
            <h3 className="text-xl font-bold mb-2">Ready to climb the ranks?</h3>
            <p className="text-white/80 mb-4">
              Invest in more shares to increase your position on the leaderboard!
            </p>
            <a
              href="/"
              className="inline-block px-6 py-3 bg-white text-[hsl(var(--brand-primary))] font-semibold rounded-lg hover:bg-gray-100 transition-colors"
            >
              Browse Available Shares
            </a>
          </div>
        )}
      </main>
    </div>
  );
}
